import { getApiOrigin } from '@/config/env';
import { getToken } from '@/utils/auth';

export type SiteRow = {
  id: string;
  name: string;
  url: string;
  createdAt: string;
  updatedAt: string;
};

function authHeaders(): HeadersInit {
  return {
    Authorization: `Bearer ${getToken()}`,
    'Content-Type': 'application/json',
  };
}

async function parseJsonError(res: Response, fallback: string): Promise<never> {
  const data = (await res.json().catch(() => ({}))) as { error?: string };
  throw new Error(data.error || fallback);
}

export async function fetchSiteList(): Promise<SiteRow[]> {
  const apiUrl = getApiOrigin();
  const res = await fetch(`${apiUrl}/api/v1/admin/sites`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  const data = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    sites?: SiteRow[];
    error?: string;
  };

  if (!res.ok || !data.ok || !Array.isArray(data.sites)) {
    throw new Error(data.error || 'Failed to load sites');
  }

  return data.sites;
}

export async function fetchSiteById(id: string): Promise<SiteRow> {
  const apiUrl = getApiOrigin();
  const res = await fetch(`${apiUrl}/api/v1/admin/sites/${encodeURIComponent(id)}`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  const data = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    site?: SiteRow;
    error?: string;
  };

  if (!res.ok || !data.ok || !data.site) {
    await parseJsonError(res, data.error || 'Failed to load site');
  }

  return data.site!;
}

export async function createSite(input: { name: string; url: string }): Promise<SiteRow> {
  const apiUrl = getApiOrigin();
  const res = await fetch(`${apiUrl}/api/v1/admin/sites`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  const data = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    site?: SiteRow;
    error?: string;
  };

  if (!res.ok || !data.ok || !data.site) {
    await parseJsonError(res, data.error || 'Failed to create site');
  }

  return data.site!;
}

export async function updateSite(
  id: string,
  input: { name?: string; url?: string }
): Promise<SiteRow> {
  const apiUrl = getApiOrigin();
  const res = await fetch(`${apiUrl}/api/v1/admin/sites/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  const data = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    site?: SiteRow;
    error?: string;
  };

  if (!res.ok || !data.ok || !data.site) {
    await parseJsonError(res, data.error || 'Failed to update site');
  }

  return data.site!;
}

export async function deleteSite(id: string): Promise<void> {
  const apiUrl = getApiOrigin();
  const res = await fetch(`${apiUrl}/api/v1/admin/sites/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  const data = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };

  if (!res.ok || !data.ok) {
    await parseJsonError(res, data.error || 'Failed to delete site');
  }
}
